import * as React from 'react';

import type { IError } from 'common/helpers/errorHelper';
import { getGraphQLErrors } from 'common/helpers/errorHelper';
import type { TrackedError } from 'ErrorBoundary/Error';
import type { ErrorState } from 'ErrorBoundary/ErrorBoundary';

import Button, { ButtonTypes } from '../core/Button/Button';
import Dialog, { type DialogProps } from '../core/Dialog/Dialog';
import Paragraph from '../core/Paragraph/Paragraph';
import Title, { TitleSizes } from '../core/Title/Title';

export interface ErrorAlertProps extends DialogProps {
  error: ErrorState;
  title?: string;
  content?: string;
}

/**
 * The ErrorAlert component
 *
 * Renders an alert that displays a provided error. Renders nothing if no error is provided.
 */
export default class ErrorAlert extends React.PureComponent<ErrorAlertProps> {
  renderErrorDialogDetails = () => {
    const source = (this.props.error.exception as TrackedError)?.extras?.source;

    const graphqlErrors: IError[] = (source && getGraphQLErrors(source)) || [];
    const details = graphqlErrors?.find(error => error.details)?.details;

    return (
      details && (
        <>
          <Title size={TitleSizes.H3} tag={TitleSizes.H2}>
            Details
          </Title>
          <Paragraph forceMultiLineHeight={true}>{details}</Paragraph>
        </>
      )
    );
  };

  renderDefaultErrorDialogContent = () => {
    return (
      <>
        <b>Something went wrong.</b> Please try repeating the same action later. In case the issue persists please
        contact our <a href="https://portal.nutanix.com/">Support</a>.
      </>
    );
  };

  public render() {
    const { cancelButtonText = 'Close', title = 'Oops!' } = this.props;

    return (
      <Dialog
        onClose={() => this.props.onClose?.()}
        isOpen={this.props.isOpen}
        hideClose={true}
        title={title}
        actions={
          <Button type={ButtonTypes.DESTRUCTIVE} onClick={this.props.onClose}>
            {cancelButtonText}
          </Button>
        }
      >
        <div className="mb-m"> {this.props.content || this.renderDefaultErrorDialogContent()}</div>

        {this.renderErrorDialogDetails()}
      </Dialog>
    );
  }
}
